<div class="flex flex-col gap-4 pb-2">
  @if (banner()) {
    <div class="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40" role="alert">
      <p class="m-0 text-sm text-red-700 dark:text-red-300">{{ banner() }}</p>
    </div>
  }

  <div class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950 space-y-6">
    <section>
      <h4 class="text-sm font-semibold text-primary m-0">Region</h4>
      <div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="sm:col-span-2">
          <label for="bank-region" class="block text-sm text-secondary">Bank country</label>
          <select
            id="bank-region"
            class="input-primary mt-2 w-full text-primary"
            [value]="values().region?.iso ?? ''"
            (change)="onRegionChange(($any($event.target)).value)">
            <optgroup label="Featured">
              @for (region of featuredRegions; track region.iso) {
                <option [value]="region.iso">{{ region.flag }} {{ region.label }} ({{ region.currency }})</option>
              }
            </optgroup>
            <optgroup label="Euro area (SEPA)">
              @for (region of euroAreaRegions; track region.iso) {
                <option [value]="region.iso">{{ region.flag }} {{ region.label }} ({{ region.currency }})</option>
              }
            </optgroup>
          </select>
          @if (fieldError('region')) {
            <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('region') }}</p>
          }
        </div>
        <div>
          <label for="entity-type" class="block text-sm text-secondary">Account belongs to</label>
          <select
            id="entity-type"
            class="input-primary mt-2 w-full text-primary"
            [value]="values().entityType"
            (change)="onEntityTypeChange($event)">
            <option value="PERSONAL">Me (personal)</option>
            <option value="COMPANY">My business</option>
          </select>
        </div>
      </div>
    </section>

    <section>
      <h4 class="text-sm font-semibold text-primary m-0">Bank details</h4>
      <div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="sm:col-span-2">
          <label for="account-name" class="block text-sm text-secondary">
            @if (values().entityType === 'COMPANY') { Registered company name } @else { Account holder name }
          </label>
          <input
            id="account-name"
            type="text"
            autocomplete="off"
            class="input-primary mt-2 w-full text-primary"
            [value]="values().accountName"
            (input)="onTextInput('accountName', $event)"
            (blur)="markTouched('accountName')" />
          @if (fieldError('accountName')) {
            <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('accountName') }}</p>
          } @else {
            <p class="m-0 mt-1.5 text-xs text-secondary">Exactly as it appears on the bank account</p>
          }
        </div>

        @if (variant() === 'GB') {
          <div>
            <label for="sort-code" class="block text-sm text-secondary">Sort code</label>
            <input
              id="sort-code"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              class="input-primary mt-2 w-full font-mono text-primary"
              [value]="formatSortCodeDisplay(values().sortCode)"
              (input)="onSortCodeInput($event)"
              (blur)="markTouched('sortCode')" />
            @if (fieldError('sortCode')) {
              <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('sortCode') }}</p>
            }
          </div>
          <div>
            <label for="account-number" class="block text-sm text-secondary">Account number</label>
            <input
              id="account-number"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              class="input-primary mt-2 w-full font-mono text-primary"
              [value]="values().accountNumber"
              (input)="onTextInput('accountNumber', $event, { digitsOnly: true, maxLength: 8 })"
              (blur)="markTouched('accountNumber')" />
            @if (fieldError('accountNumber')) {
              <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('accountNumber') }}</p>
            }
          </div>
        }

        @if (variant() === 'US') {
          <div>
            <label for="routing-number" class="block text-sm text-secondary">Routing number</label>
            <input
              id="routing-number"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              class="input-primary mt-2 w-full font-mono text-primary"
              [value]="values().routingNumber"
              (input)="onTextInput('routingNumber', $event, { digitsOnly: true, maxLength: 9 })"
              (blur)="markTouched('routingNumber')" />
            @if (fieldError('routingNumber')) {
              <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('routingNumber') }}</p>
            }
          </div>
          <div>
            <label for="us-account-number" class="block text-sm text-secondary">Account number</label>
            <input
              id="us-account-number"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              class="input-primary mt-2 w-full font-mono text-primary"
              [value]="values().accountNumber"
              (input)="onTextInput('accountNumber', $event, { digitsOnly: true, maxLength: 17 })"
              (blur)="markTouched('accountNumber')" />
            @if (fieldError('accountNumber')) {
              <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('accountNumber') }}</p>
            }
          </div>
          <div>
            <label for="account-category" class="block text-sm text-secondary">Account type</label>
            <select
              id="account-category"
              class="input-primary mt-2 w-full text-primary"
              [value]="values().category"
              (change)="onCategoryChange($event)">
              <option value="Checking">Checking</option>
              <option value="Savings">Savings</option>
            </select>
            @if (fieldError('category')) {
              <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('category') }}</p>
            }
          </div>
        }

        @if (variant() === 'EUR') {
          <div class="sm:col-span-2">
            <label for="iban" class="block text-sm text-secondary">IBAN</label>
            <input
              id="iban"
              type="text"
              autocomplete="off"
              class="input-primary mt-2 w-full font-mono text-primary"
              [value]="formatIbanDisplay(values().iban)"
              (input)="onTextInput('iban', $event, { normalizeIban: true, maxLength: 34 })"
              (blur)="markTouched('iban')" />
            @if (fieldError('iban')) {
              <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('iban') }}</p>
            } @else {
              <p class="m-0 mt-1.5 text-xs text-secondary">Euro (SEPA) payouts are sent to this IBAN</p>
            }
          </div>
        }
      </div>
    </section>

    @if (needsAddress()) {
      <section>
        <h4 class="text-sm font-semibold text-primary m-0">
          @if (variant() === 'US') { Billing address } @else { Mailing address }
        </h4>
        <div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="sm:col-span-2">
            <label for="street" class="block text-sm text-secondary">Street address</label>
            <input
              id="street"
              type="text"
              autocomplete="street-address"
              class="input-primary mt-2 w-full text-primary"
              [value]="values().street"
              (input)="onTextInput('street', $event)"
              (blur)="markTouched('street')" />
            @if (fieldError('street')) {
              <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('street') }}</p>
            }
          </div>
          <div>
            <label for="city" class="block text-sm text-secondary">City</label>
            <input
              id="city"
              type="text"
              autocomplete="address-level2"
              class="input-primary mt-2 w-full text-primary"
              [value]="values().city"
              (input)="onTextInput('city', $event)"
              (blur)="markTouched('city')" />
            @if (fieldError('city')) {
              <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('city') }}</p>
            }
          </div>
          @if (variant() === 'US') {
            <div>
              <label for="state" class="block text-sm text-secondary">State</label>
              <input
                id="state"
                type="text"
                autocomplete="address-level1"
                class="input-primary mt-2 w-full text-primary"
                [value]="values().state"
                (input)="onTextInput('state', $event)"
                (blur)="markTouched('state')" />
              @if (fieldError('state')) {
                <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('state') }}</p>
              }
            </div>
          }
          <div>
            <label for="zip" class="block text-sm text-secondary">
              @if (variant() === 'US') { ZIP code } @else { Eircode }
            </label>
            <input
              id="zip"
              type="text"
              [attr.inputmode]="variant() === 'US' ? 'numeric' : null"
              autocomplete="postal-code"
              class="input-primary mt-2 w-full text-primary"
              [value]="values().zip"
              (input)="onTextInput('zip', $event, { maxLength: variant() === 'US' ? 10 : 8 })"
              (blur)="markTouched('zip')" />
            @if (fieldError('zip')) {
              <p class="m-0 mt-1.5 text-sm text-red-600">{{ fieldError('zip') }}</p>
            }
          </div>
        </div>
      </section>
    }

    <div class="flex justify-end gap-2">
      <button type="button" class="secondary-button" [disabled]="saving()" (click)="onCancel()">Cancel</button>
      <button type="button" class="primary-button" [disabled]="saving()" (click)="onSave()">
        {{ saveLabel() }}
      </button>
    </div>
  </div>
</div>
